import { useEffect, useId, useRef, useState } from "react";
import { request } from "../api/client";
import { ErrorNotice } from "../components/AdminFeedback";
import { FieldRequirement } from "../components/FieldRequirement";
import { useLocale } from "../i18n";
import "../styles/role-account.css";

type Person = { id: string; name: string };
export function RoleAccountPicker({ unitId, disabled, value, onChange }: {
  unitId: string; disabled: boolean; value: string; onChange: (id: string | undefined, person?: Person) => void;
}) {
  const { w } = useLocale(); const id = useId();
  const [search, setSearch] = useState(""); const [person, setPerson] = useState<Person>();
  const [searched, setSearched] = useState(false); const [busy, setBusy] = useState(false);
  const [invalid, setInvalid] = useState(false); const [error, setError] = useState<unknown>();
  const active = useRef<AbortController | null>(null);
  useEffect(() => () => active.current?.abort(), []);
  function change(text: string) {
    active.current?.abort(); active.current = null; setBusy(false); setSearch(text);
    setPerson(undefined); setSearched(false); setInvalid(false); setError(undefined); onChange(undefined);
  }
  async function lookup() {
    if (disabled) return;
    const target = search.trim();
    if (!/^[A-Za-z0-9][A-Za-z0-9_.-]{0,95}$/.test(target)) { setInvalid(true); return; }
    active.current?.abort(); const controller = new AbortController(); active.current = controller;
    setBusy(true); setError(undefined); setInvalid(false); setPerson(undefined); setSearched(false); onChange(undefined);
    try {
      const result = await request<{ unitId: string; recipient?: Person | null }>("/events/eventRoleInvitation/options?" + new URLSearchParams({ unitId, individualId: target }), "GET", undefined, controller.signal);
      if (controller.signal.aborted) return;
      if (result.unitId !== unitId || result.recipient && (result.recipient.id !== target || typeof result.recipient.name !== "string")) throw new Error("Invalid account lookup response");
      setPerson(result.recipient || undefined); setSearched(true);
    } catch (e) { if (!controller.signal.aborted) setError(e); }
    finally { if (!controller.signal.aborted) setBusy(false); }
  }
  return <div className="role-account-picker" data-field-path="individualId">
    <label htmlFor={id}><span className="field-caption">{w("معرّف الحساب (ID)", "Account ID")} <FieldRequirement required /></span></label>
    <div className="role-account-search">
      <input id={id} value={search} disabled={disabled} maxLength={96} dir="ltr" autoComplete="off" aria-invalid={invalid}
        aria-describedby={`${id}-help${invalid ? ` ${id}-error` : ""}`} onChange={event => change(event.target.value)}
        onKeyDown={event => { if (event.key === "Enter") { event.preventDefault(); void lookup(); } }} />
      <button type="button" className="secondary" disabled={disabled || busy} onClick={() => void lookup()}>{busy ? w("جارٍ البحث…", "Searching…") : w("بحث", "Search")}</button>
    </div>
    <small id={`${id}-help`} className="event-field-help">{w("أدخل معرّف الحساب ثم ابحث واختر المستخدم من النتيجة. يظهر الاسم للتأكيد ولا يُكتب يدوياً.", "Enter the account ID, search, then select the matching user. The name is displayed for confirmation, not entered manually.")}</small>
    {invalid && <small id={`${id}-error`} className="event-field-error">{w("أدخل معرّف الحساب الصحيح، وليس اسم المستخدم.", "Enter a valid account ID, not a user name.")}</small>}
    <ErrorNotice error={error} operation="read" />
    {person && <label className="role-account-result"><input type="radio" name={`${id}-person`} disabled={disabled || busy} checked={value === person.id} onChange={() => onChange(person.id, person)} />
      <span className="role-account-identity"><strong>{person.name}</strong><bdi>{person.id}</bdi></span>
      <span>{value === person.id ? w("تم الاختيار", "Selected") : w("اختيار الحساب", "Select account")}</span>
    </label>}
    {searched && !person && <p role="status">{w("لا يوجد حساب متاح بهذا المعرّف. تأكد من الـID وأعد البحث.", "No available account matches this ID. Check the ID and search again.")}</p>}
  </div>;
}
